<template>
    <div class="BillingPlan">
        <div class="yourPlan">
            <div class="main" v-if="subscription">
                <h2>{{ drivers }} {{ drivers !== 1 ? 'drivers' : 'driver' }}</h2>

                <p class="subscriptionStatus">
                    Status:
                    <strong v-if="subscription.isCanceled" class="canceled">Canceled</strong>
                    <strong v-else-if="subscription.status === 'trialing'">Free Trial</strong>
                    <strong v-else :class="subscription.status">{{ subscription.status.replace(/_/g, ' ') }}</strong>
                </p>

                <p v-if="['past_due', 'unpaid'].includes(subscription.status) || subscription.isCanceled">
                    Suspends on {{ subscription.graceEndsAt | formatDate }}
                </p>

                <p v-if="subscription.status === 'trialing'">
                    Expires on {{ subscription.endsAt | formatDate }}
                </p>
            </div>

            <div class="aside">
                <div class="input">
                    <FormNumber
                        v-model="newDrivers"
                        class="marginless"
                        :min="1"
                        :max="5000"
                        @input="estimateVisible = true"
                    />
                    <Button
                        v-if="subscriptionCanBeUpdated"
                        class="primary"
                        @click="handleUpdateSubscription"
                        :disabled="submitPending || newDrivers === drivers || tooManyActiveDrivers"
                    >Update</Button>
                    <Button
                        v-else
                        class="primary"
                        @click="handleCreateSubscription"
                        :disabled="submitPending"
                    >Subscribe</Button>
                </div>

                <p v-show="tooManyActiveDrivers" class="error">
                    You have too many active drivers for the new subscription. Please disable at least {{ activeDrivers.length - newDrivers }} before changing your subscription.
                </p>

                <div v-show="newDrivers !== drivers || !subscriptionCanBeUpdated" class="totalPrice">
                    <h5>New estimated monthly charges</h5>
                    <div class="cost">
                        <strong>{{ this.newDrivers * this.planCost | formatCurrency }}</strong>/mo
                    </div>
                </div>
            </div>
        </div>

        <div class="estimate totalPrice">
            <h5 v-if="subscription.status === 'trialing'">Estimated charge at end of trial</h5>
            <h5 v-else>Estimated costs for this billing period</h5>
            <div class="cost">
                <strong>{{ drivers * this.planCost | formatCurrency }}</strong>
            </div>

            <span>These numbers aren't final until your billing cycle is over.</span>
        </div>

        <div class="updateSubscriptionBtns">
            <div v-if="subscriptionCanBeUpdated">
                <Button
                    v-if="!subscription.isCanceled"
                    class="link danger"
                    :disabled="submitPending"
                    @click="handleCancelSubscription"
                >
                    Cancel {{ subscription.status === 'trialing' ? 'Trial' : 'Subscription' }}
                </Button>
                <Button
                    v-else
                    class="link primary"
                    :disabled="submitPending"
                    @click="handleResumeSubscription"
                >
                    Resume Subscription
                </Button>
            </div>
            <div v-else-if="['past_due', 'unpaid'].includes(subscription.status)">
                <Button
                    class="link danger"
                    :disabled="submitPending"
                    @click="handleConfirmSubscription"
                >
                    Retry Payment
                </Button>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import { mapState, mapGetters } from 'vuex';
import ConfirmModal from '@/components/modals/ConfirmModal';
import { getStripeProduct } from '@/utils/getStripeProduct';

export default {
    name: 'BillingPlan',

    components: { ConfirmModal },

    data: () => ({
        newDrivers: null,
        submitPending: false,
    }),

    computed: {
        ...mapState('company', ['subscription']),
        ...mapGetters('company', ['activeDrivers']),
        ...mapGetters('auth', ['company']),

        planCost() {
            const product = getStripeProduct(this.company.createdAt, this.subscription.status);
            return product.price;
        },

        subscriptionCanBeUpdated() {
            return ['trialing', 'active', 'incomplete'].includes(this.subscription.status);
        },

        drivers() {
            return this.subscription?.quantity ?? 0;
        },

        tooManyActiveDrivers() {
            return this.activeDrivers.length > this.newDrivers;
        },
    },

    watch: {
        drivers() {
            this.newDrivers = this.drivers;
        },
    },

    mounted() {
        this.newDrivers = this.drivers ?? null;
    },

    methods: {
        async handleUpdateSubscription() {
            if (this.submitPending || this.tooManyActiveDrivers) return;

            this.submitPending = true;

            const product = getStripeProduct(this.company.createdAt, this.subscription.status);
            const res = await api.updateSubscription({
                id: this.subscription.id,
                item: { plan: product.id, quantity: this.newDrivers },
            });

            if (res.error) {
                this.submitPending = false;
                return this.$awn.alert('Failed to update subscription');
            }

            this.waitForSubscriptionUpdate();
        },

        async handleCreateSubscription() {
            if (this.submitPending) return;

            this.submitPending = true;
            const res = await api.createSubscription({ plan: process.env.VUE_APP_STRIPE_DEFAULT_PLAN_ID, quantity: this.newDrivers });
            if (res.error) {
                this.submitPending = false;
                return this.$awn.alert('Failed to create subscription');
            }

            this.waitForSubscriptionUpdate();
        },

        async handleCancelSubscription() {
            const title = 'Account Cancellation';
            const message = 'Are you sure you want to cancel your DumpTek account? This will suspend  billing and all managers and drivers will lose access your company.';

            const onConfirm = async () => {
                if (this.submitPending) return;

                this.submitPending = true;
                const res = await api.cancelSubscription();
                if (res.error) {
                    this.submitPending = false;
                    return this.$awn.alert('Failed to cancel subscription. Please contact DumpTek for assistance.');
                }

                this.waitForSubscriptionUpdate();
            };

            this.$modal.show(
                ConfirmModal,
                { title, message, type: 'delete', confirmLabel: 'Cancel Account', cancelLabel: 'Nevermind', onConfirm },
                { height: 'auto' },
            );
        },

        async handleResumeSubscription() {
            if (this.submitPending) return;

            this.submitPending = true;
            const res = await api.resumeSubscription();
            if (res.error) {
                this.submitPending = false;
                return this.$awn.alert('Failed to resume subscription');
            }

            this.waitForSubscriptionUpdate();
        },

        async handleConfirmSubscription() {
            if (this.submitPending) return;

            this.submitPending = true;
            const res = await api.confirmSubscription();
            if (res.error) {
                this.submitPending = false;
                return this.$awn.alert('Failed to confirm subscription');
            }

            this.waitForSubscriptionUpdate();
        },

        async waitForSubscriptionUpdate() {
            let updateAttempts = 0;
            const lastUpdatedAt = this.subscription.updatedAt;

            const updateOverview = async () => {
                updateAttempts += 1;
                await this.$store.dispatch('company/fetchOverview');

                if (this.$store.state.company.subscription.updatedAt !== lastUpdatedAt) {
                    this.submitPending = false;
                    this.$awn.success('Subscription has been updated');
                    return;
                }

                // Give up after 16 attempts.
                if (updateAttempts >= 16) {
                    this.submitPending = false;
                    return this.$awn.alert('We\'re having problems confirming your payment, wait a couple of minutes and refresh the browser. If you\'re still having issues please contact support.');
                }

                // Keep trying until the subscription has been updated.
                setTimeout(updateOverview, 1500);
            };

            setTimeout(updateOverview, 1500);
        }
    },
}
</script>

<style lang="scss" scoped>
@import 'globals.scss';

.BillingPlan {
    .yourPlan {
        display: flex;
        justify-content: space-between;

        @include breakpoint(lg) {
            flex-direction: column;
        }
    }

    .main {
        h2 {
            margin-bottom: 5px;
            font-weight: $font-weight-bold;
        }
    }

    .totalPrice {
        .cost {
            font-size: 28px;
        }
    }

    .estimate {
        border: 1px solid $divider-color;
        padding: $grid-gutter-width;
        margin-top: 30px;

        span {
            font-size: 14px;
        }
    }

    .subscriptionStatus {
        text-transform: capitalize;

        strong.active,
        strong.trailing {
            color: $green-color;
        }

        strong.incomplete_expired,
        strong.past_due,
        strong.canceled,
        strong.unpaid {
            color: $red-color;
        }
    }

    .input {
        display: flex;
        justify-content: flex-end;

        .FormNumber {
            text-align: center;
            font-size: 22px;

            ::v-deep input  {
                border-right: 0;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }
        }

        .Button {
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
        }

        @include breakpoint(lg) {
            justify-content: flex-start;
        }
    }

    .aside {
        width: 480px;
        margin-top: 10px;
        text-align: right;

        .totalPrice {
            margin-top: $grid-gutter-width;
        }

        @include breakpoint(lg) {
            text-align: left;
        }
    }

    .error {
        margin-top: $grid-gutter-width;
        color: $red-color;
    }

    .updateSubscriptionBtns {
        margin-top: 30px;

        .Button {
            padding: 0;
        }
    }
}
</style>
@/utils/getStripeProduct